import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { trainingRequestsAPI, partnersAPI, coursesAPI, sessionsAPI, feedbackAPI, trainersAPI, reportsAPI, purchaseOrdersAPI, taxonomyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Save, Edit2, MessageSquare, Mail, Lock, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { EmailReportDialog } from '../components/shared/EmailReportDialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { DetailsTab } from '../components/training-project/DetailsTab';
import { SessionsTab } from '../components/training-project/SessionsTab';
import { AttendanceTab } from '../components/training-project/AttendanceTab';
import { FeedbackTab } from '../components/training-project/FeedbackTab';
import { SessionFormDialog } from '../components/training-project/SessionFormDialog';
import { SessionFeedbackDialog } from '../components/training-project/SessionFeedbackDialog';
import { ProjectFeedbackDialog } from '../components/training-project/ProjectFeedbackDialog';
import { BulkImportSessionsDialog } from '../components/training-project/BulkImportSessionsDialog';

const STATUS_COLORS = {
  requested: 'bg-yellow-100 text-yellow-800',
  planning: 'bg-slate-100 text-slate-800',
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-violet-100 text-violet-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const EMPTY_SESSION_FORM = {
  title: '', session_date: '', start_time: '09:00', end_time: '17:00', session_type: 'virtual',
  is_multi_day: false, end_date: '', course_id: '', course_title: '', content_item_id: '', content_item_title: '', meeting_link: '', platform: 'Teams',
  venue: '', address: '', trainer_id: '', trainer_name: '', trainer_email: '', trainer_2_name: '', trainer_2_email: '',
  session_trainers: [{ trainer_id: '', name: '', email: '' }], contract_id: '', contract_issued: false, notes: '',
  session_cost: '', amount_invoiced: '', session_po_number: '', session_po_line_item_id: '', session_po_line_item_description: '', invoiced_month: '', expected_count: '',
  custom_fields: {},
};

export const TrainingProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [partner, setPartner] = useState(null);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [availablePos, setAvailablePos] = useState([]);
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'details');
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);

  // Feedback state
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [canComplete, setCanComplete] = useState(null);
  const [sessionFeedbackDialogOpen, setSessionFeedbackDialogOpen] = useState(false);
  const [projectFeedbackDialogOpen, setProjectFeedbackDialogOpen] = useState(false);
  const [selectedSessionForFeedback, setSelectedSessionForFeedback] = useState(null);

  // Bulk Import state
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);

  // Complete/Reopen guard state
  const [completeWarningOpen, setCompleteWarningOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  // Report modal
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportHtml, setReportHtml] = useState('');

  const [editData, setEditData] = useState({
    title: '', description: '', status: '', pm_name: '',
    registered_attendees: 0, actual_attendees: 0, notes: '',
  });

  const [sessionForm, setSessionForm] = useState(EMPTY_SESSION_FORM);

  // Session Feedback Form
  const [sessionFeedbackForm, setSessionFeedbackForm] = useState({
    mtm_trainer_rating: '', mtm_course_rating: '', verbatims: '', highlights: '', improvements: '',
  });

  // Project Feedback Form
  const [projectFeedbackForm, setProjectFeedbackForm] = useState({
    key_achievements: '', challenges_faced: '', lessons_learned: '',
    partner_feedback_summary: '', recommendations: '', mtm_feedback_score: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const projectData = await trainingRequestsAPI.getById(id);
      setProject(projectData);
      setEditData({
        title: projectData.title || '',
        description: projectData.description || '',
        status: projectData.status || 'requested',
        pm_name: projectData.pm_name || '',
        registered_attendees: projectData.registered_attendees || 0,
        actual_attendees: projectData.actual_attendees || 0,
        notes: projectData.notes || '',
      });

      // Independent fetches — run in parallel instead of sequentially (was ~10s serial load)
      const [
        partnerData, coursesData, sessionsData, trainersData, posData, feedbackResult, taxonomyData,
      ] = await Promise.all([
        projectData.partner_id ? partnersAPI.getById(projectData.partner_id).catch(() => null) : Promise.resolve(null),
        coursesAPI.getAll().catch(() => []),
        sessionsAPI.getByProject(id).catch(() => []),
        trainersAPI.getAll({ is_active: true }).catch(() => []),
        purchaseOrdersAPI.getAll().catch(() => []),
        Promise.all([feedbackAPI.getFeedbackSummary(id), feedbackAPI.canMarkComplete(id)]).catch(() => [null, null]),
        taxonomyAPI.getAll().catch(() => []),
      ]);

      if (partnerData) setPartner(partnerData);
      setCourses(coursesData);
      setSessions(sessionsData);
      setTrainers(trainersData);
      setAvailablePos(posData.filter(p => p.status === 'active' || p.status === 'exhausted'));
      setTaxonomyCategories(taxonomyData);
      const [summary, completion] = feedbackResult;
      if (summary) setFeedbackSummary(summary);
      if (completion) setCanComplete(completion);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load training project');
      navigate('/training-projects');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const futureScheduledSessions = sessions.filter(
    s => s.session_date && s.session_date >= todayStr && s.status !== 'cancelled' && s.status !== 'completed'
  );

  const doSaveProject = async (payload) => {
    setSaving(true);
    try {
      await trainingRequestsAPI.update(id, payload);
      toast.success('Training project updated');
      setIsEditing(false);
      setCompleteWarningOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update training project');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const isCompletingNow = editData.status === 'completed' && project.status !== 'completed';
    if (isCompletingNow && futureScheduledSessions.length > 0) {
      setCompleteWarningOpen(true);
      return;
    }
    doSaveProject(editData);
  };

  const handleReopenProject = async () => {
    try {
      await trainingRequestsAPI.update(id, { status: 'in_progress' });
      toast.success('Project reopened — status set back to In Progress');
      setReopenConfirmOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to reopen project:', error);
      toast.error('Failed to reopen project');
    }
  };

  const openSessionFeedbackDialog = (session) => {
    setSelectedSessionForFeedback(session);
    const existingFeedback = feedbackSummary?.session_feedback?.find(
      f => f.session_id === session.id
    );
    if (existingFeedback) {
      setSessionFeedbackForm({
        mtm_trainer_rating: existingFeedback.mtm_trainer_rating?.toString() || '',
        mtm_course_rating: existingFeedback.mtm_course_rating?.toString() || '',
        verbatims: existingFeedback.verbatims || '',
        highlights: existingFeedback.highlights || '',
        improvements: existingFeedback.improvements || '',
      });
    } else {
      setSessionFeedbackForm({
        mtm_trainer_rating: '', mtm_course_rating: '', verbatims: '', highlights: '', improvements: '',
      });
    }
    setSessionFeedbackDialogOpen(true);
  };

  const openProjectFeedbackDialog = () => {
    if (feedbackSummary?.project_feedback) {
      const pf = feedbackSummary.project_feedback;
      setProjectFeedbackForm({
        key_achievements: pf.key_achievements || '',
        challenges_faced: pf.challenges_faced || '',
        lessons_learned: pf.lessons_learned || '',
        partner_feedback_summary: pf.partner_feedback_summary || '',
        recommendations: pf.recommendations || '',
        mtm_feedback_score: pf.mtm_feedback_score?.toString() || '',
      });
    } else {
      setProjectFeedbackForm({
        key_achievements: '', challenges_faced: '', lessons_learned: '',
        partner_feedback_summary: '', recommendations: '', mtm_feedback_score: '',
      });
    }
    setProjectFeedbackDialogOpen(true);
  };

  const handleSaveSessionFeedback = async () => {
    try {
      const existingFeedback = feedbackSummary?.session_feedback?.find(
        f => f.session_id === selectedSessionForFeedback.id
      );

      const payload = {
        mtm_trainer_rating: sessionFeedbackForm.mtm_trainer_rating !== '' ? parseFloat(sessionFeedbackForm.mtm_trainer_rating) : null,
        mtm_course_rating: sessionFeedbackForm.mtm_course_rating !== '' ? parseFloat(sessionFeedbackForm.mtm_course_rating) : null,
        verbatims: sessionFeedbackForm.verbatims || null,
        highlights: sessionFeedbackForm.highlights || null,
        improvements: sessionFeedbackForm.improvements || null,
      };

      if (existingFeedback) {
        await feedbackAPI.updateSessionFeedback(selectedSessionForFeedback.id, payload);
        toast.success('Session feedback updated');
      } else {
        await feedbackAPI.createSessionFeedback({
          session_id: selectedSessionForFeedback.id,
          training_project_id: id,
          ...payload,
        }, user?.id);
        toast.success('Session feedback submitted');
      }

      // Auto-complete the project only once every (non-cancelled) session has feedback —
      // do NOT auto-complete just because a single session's feedback was saved.
      const relevantSessions = sessions.filter(s => s.status !== 'cancelled');
      const feedbackSessionIds = new Set(
        (feedbackSummary?.session_feedback || []).map(f => f.session_id)
      );
      feedbackSessionIds.add(selectedSessionForFeedback.id); // this submission just added feedback
      const allSessionsHaveFeedback = relevantSessions.length > 0 &&
        relevantSessions.every(s => feedbackSessionIds.has(s.id));

      if (allSessionsHaveFeedback && project?.status !== 'completed') {
        await trainingRequestsAPI.update(id, { status: 'completed' });
        toast.success('All session feedback received — project automatically marked as Completed');
      }

      setSessionFeedbackDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save session feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to save feedback');
    }
  };

  const handleSaveProjectFeedback = async () => {
    try {
      const payload = {
        training_project_id: id,
        key_achievements: projectFeedbackForm.key_achievements || null,
        challenges_faced: projectFeedbackForm.challenges_faced || null,
        lessons_learned: projectFeedbackForm.lessons_learned || null,
        partner_feedback_summary: projectFeedbackForm.partner_feedback_summary || null,
        recommendations: projectFeedbackForm.recommendations || null,
        mtm_feedback_score: projectFeedbackForm.mtm_feedback_score ? parseFloat(projectFeedbackForm.mtm_feedback_score) : null,
      };

      if (feedbackSummary?.has_project_feedback) {
        await feedbackAPI.updateProjectFeedback(id, payload);
        toast.success('Project feedback updated');
      } else {
        await feedbackAPI.createProjectFeedback(payload, user?.id);
        toast.success('Project feedback submitted');
      }

      // Auto-complete the project when feedback is added
      if (project?.status !== 'completed') {
        await trainingRequestsAPI.update(id, { status: 'completed' });
        toast.success('Project automatically marked as Completed');
      }

      setProjectFeedbackDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save project feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to save feedback');
    }
  };

  const openSessionDialog = (session = null) => {
    if (!session && project.status === 'completed') {
      toast.error('This project is completed — new sessions cannot be added');
      return;
    }
    if (session) {
      setEditingSession(session);
      setSessionForm({
        title: session.title || '',
        session_date: session.session_date || '',
        start_time: session.start_time || '09:00',
        end_time: session.end_time || '17:00',
        session_type: session.session_type || 'virtual',
        is_multi_day: session.is_multi_day || false,
        end_date: session.end_date || '',
        course_id: session.course_id || '',
        course_title: session.course_title || '',
        content_item_id: session.content_item_id || '',
        content_item_title: session.content_item_title || '',
        meeting_link: session.meeting_link || '',
        platform: session.platform || 'Teams',
        venue: session.venue || '',
        address: session.address || '',
        trainer_name: session.trainer_name || '',
        trainer_email: session.trainer_email || '',
        trainer_id: session.trainer_id || '',
        trainer_2_name: session.trainer_2_name || '',
        trainer_2_email: session.trainer_2_email || '',
        session_trainers: (() => {
          // Build from session_trainers if present, else fall back to legacy fields
          const matchRoster = (slot) => {
            if (slot.trainer_id) return slot;
            const match = trainers.find(t => t.name?.toLowerCase().trim() === slot.name?.toLowerCase().trim());
            return match ? { ...slot, trainer_id: match.id, email: slot.email || match.email || '' } : slot;
          };
          if (session.session_trainers && session.session_trainers.length > 0) {
            return session.session_trainers.map(matchRoster);
          }
          const slots = [];
          if (session.trainer_name) slots.push(matchRoster({ trainer_id: session.trainer_id || '', name: session.trainer_name, email: session.trainer_email || '' }));
          if (session.trainer_2_name) slots.push(matchRoster({ trainer_id: '', name: session.trainer_2_name, email: session.trainer_2_email || '' }));
          return slots.length > 0 ? slots : [{ trainer_id: '', name: '', email: '' }];
        })(),
        contract_id: session.contract_id || '',
        contract_issued: session.contract_issued || false,
        notes: session.notes || '',
        session_cost: session.session_cost != null ? String(session.session_cost) : '',
        amount_invoiced: session.amount_invoiced != null ? String(session.amount_invoiced) : '',
        session_po_number: session.session_po_number || '',
        session_po_line_item_id: session.session_po_line_item_id || '',
        session_po_line_item_description: session.session_po_line_item_description || '',
        invoiced_month: session.invoiced_month || '',
        expected_count: session.expected_count != null ? String(session.expected_count) : '',
        custom_fields: session.custom_fields || {},
      });
    } else {
      setEditingSession(null);
      setSessionForm({ ...EMPTY_SESSION_FORM, session_date: new Date().toISOString().split('T')[0] });
    }
    setConflictWarning(null);
    setRecommendations([]);
    setSessionDialogOpen(true);
  };

  // Fetch trainer recommendations when course changes
  const fetchRecommendations = async (courseId, courseTitleVal, sessionType) => {
    if (!courseId && !courseTitleVal) { setRecommendations([]); return; }
    try {
      const data = await trainersAPI.getRecommendations({
        course_id: courseId || undefined,
        course_title: !courseId && courseTitleVal ? courseTitleVal : undefined,
        session_type: sessionType,
      });
      setRecommendations(data.filter(r => r.trainer?.is_active));
    } catch { setRecommendations([]); }
  };

  // Check trainer conflict when trainer + date + times are set
  const checkConflict = async (trainerId, sessionDate, startTime, endTime, excludeId) => {
    if (!trainerId || !sessionDate || !startTime || !endTime) { setConflictWarning(null); return; }
    try {
      const result = await trainersAPI.checkConflict(trainerId, {
        session_date: sessionDate, start_time: startTime, end_time: endTime,
        exclude_session_id: excludeId,
      });
      setConflictWarning(result.has_conflict ? result : null);
    } catch { setConflictWarning(null); }
  };

  const handleSelectRecommendedTrainer = (rec) => {
    const t = rec.trainer;
    setSessionForm(f => ({
      ...f,
      trainer_id: t.id,
      trainer_name: t.name,
      trainer_email: t.email,
    }));
    checkConflict(t.id, sessionForm.session_date, sessionForm.start_time, sessionForm.end_time, editingSession?.id);
  };

  const handleSaveSession = async () => {
    if (!sessionForm.title || !sessionForm.session_date) {
      toast.error('Session title and date are required');
      return;
    }

    try {
      // Sync session_trainers back to legacy fields for backward compat
      const activeTrainers = sessionForm.session_trainers.filter(t => t.name.trim());
      const payload = {
        ...sessionForm,
        training_project_id: id,
        end_date: sessionForm.is_multi_day ? sessionForm.end_date : null,
        session_trainers: activeTrainers,
        trainer_id: activeTrainers[0]?.trainer_id || '',
        trainer_name: activeTrainers[0]?.name || '',
        trainer_email: activeTrainers[0]?.email || '',
        trainer_2_name: activeTrainers[1]?.name || '',
        trainer_2_email: activeTrainers[1]?.email || '',
        session_cost: sessionForm.session_cost !== '' ? parseFloat(sessionForm.session_cost) : null,
        amount_invoiced: sessionForm.amount_invoiced !== '' ? parseFloat(sessionForm.amount_invoiced) : null,
        session_po_number: sessionForm.session_po_number || null,
        session_po_line_item_id: sessionForm.session_po_line_item_id || null,
        session_po_line_item_description: sessionForm.session_po_line_item_description || null,
        invoiced_month: sessionForm.invoiced_month || null,
        expected_count: sessionForm.expected_count !== '' ? parseInt(sessionForm.expected_count) : null,
        custom_fields: sessionForm.custom_fields || {},
      };

      if (editingSession) {
        await sessionsAPI.update(editingSession.id, payload);
        toast.success('Session updated');
      } else {
        await sessionsAPI.create(payload);
        toast.success('Session created');
      }
      setSessionDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save session:', error);
      toast.error('Failed to save session');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      await sessionsAPI.delete(sessionId);
      toast.success('Session deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error('Failed to delete session');
    }
  };

  const handleCopySession = async (session) => {
    if (project.status === 'completed') {
      toast.error('This project is completed — sessions cannot be duplicated');
      return;
    }
    try {
      const payload = {
        training_project_id: session.training_project_id,
        title: `Copy of ${session.title}`,
        session_date: session.session_date,
        start_time: session.start_time,
        end_time: session.end_time,
        session_type: session.session_type,
        is_multi_day: session.is_multi_day || false,
        end_date: session.end_date || null,
        course_id: session.course_id || null,
        course_title: session.course_title || null,
        meeting_link: session.meeting_link || null,
        platform: session.platform || null,
        venue: session.venue || null,
        address: session.address || null,
        trainer_id: session.trainer_id || null,
        trainer_name: session.trainer_name || null,
        trainer_email: session.trainer_email || null,
        notes: session.notes || null,
      };
      await sessionsAPI.create(payload);
      toast.success('Session duplicated successfully');
      loadData();
    } catch (error) {
      console.error('Failed to copy session:', error);
      toast.error('Failed to copy session');
    }
  };

  const handleDownloadTemplate = () => {
    window.open(sessionsAPI.downloadTemplate(), '_blank');
  };

  const handleBulkImport = async () => {
    if (!bulkImportFile) return;
    setBulkImportLoading(true);
    try {
      const results = await sessionsAPI.bulkImport(id, bulkImportFile);
      setBulkImportResults(results);
      if (results.successful > 0) {
        toast.success('Sessions imported');
        loadData();
      }
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setBulkImportLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setReportOpen(true); setReportLoading(true); setReportHtml('');
    try {
      const r = await reportsAPI.generatePrivateDeliveryReport(id);
      setReportHtml(r.html);
    } catch {
      toast.error('Report generation failed'); setReportOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-64 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Training project not found</h2>
        <Button onClick={() => navigate('/training-projects')} className="mt-4">
          Back to Training Projects
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-project-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/training-projects')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold">{project.title}</h1>
            <p className="text-muted-foreground">Training Project Details</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={STATUS_COLORS[project.status] || ''} data-testid="project-status-badge">
            {project.status === 'completed' && <Lock className="w-3 h-3 mr-1 inline" />}
            {project.status?.replace('_', ' ')}
          </Badge>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={handleGenerateReport} data-testid="generate-report-btn" className="hidden sm:flex">
              <Mail className="w-4 h-4 mr-2" />Generate Report
            </Button>
          )}
          {!isEditing && project.status === 'completed' && isAdmin() && (
            <Button variant="outline" size="sm" onClick={() => setReopenConfirmOpen(true)} data-testid="reopen-project-btn">
              <RotateCcw className="w-4 h-4 mr-2" />Reopen
            </Button>
          )}
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} data-testid="edit-button">
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-project-button">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={completeWarningOpen} onOpenChange={setCompleteWarningOpen}>
        <AlertDialogContent data-testid="complete-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Sessions still scheduled
            </AlertDialogTitle>
            <AlertDialogDescription>
              This project has {futureScheduledSessions.length} session{futureScheduledSessions.length > 1 ? 's' : ''} still scheduled on or after today. Marking the project Completed will block adding, importing or duplicating any further sessions. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="complete-warning-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="complete-warning-confirm" onClick={() => doSaveProject(editData)}>
              Mark Completed Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reopenConfirmOpen} onOpenChange={setReopenConfirmOpen}>
        <AlertDialogContent data-testid="reopen-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set the status back to "In Progress" and re-enable adding, importing and duplicating sessions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reopen-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="reopen-confirm-action" onClick={handleReopenProject}>
              Reopen Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="sessions">Sessions ({sessions.length})</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="feedback" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Feedback
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <DetailsTab project={project} partner={partner} isEditing={isEditing} editData={editData} setEditData={setEditData} />
        </TabsContent>

        <TabsContent value="sessions" className="space-y-6">
          <SessionsTab
            sessions={sessions}
            courses={courses}
            highlightSessionId={location.state?.highlightSessionId}
            onAddSession={() => openSessionDialog()}
            onEditSession={openSessionDialog}
            onCopySession={handleCopySession}
            onDeleteSession={handleDeleteSession}
            onBulkImportOpen={() => {
              if (project.status === 'completed') {
                toast.error('This project is completed — sessions cannot be imported');
                return;
              }
              setBulkImportDialogOpen(true);
            }}
            isCompleted={project.status === 'completed'}
          />
        </TabsContent>

        <TabsContent value="attendance">
          <AttendanceTab sessions={sessions} project={project} onSave={loadData} />
        </TabsContent>

        <TabsContent value="feedback" className="space-y-6">
          <FeedbackTab
            sessions={sessions}
            feedbackSummary={feedbackSummary}
            canComplete={canComplete}
            onOpenSessionFeedback={openSessionFeedbackDialog}
            onOpenProjectFeedback={openProjectFeedbackDialog}
          />
        </TabsContent>
      </Tabs>

      <SessionFormDialog
        open={sessionDialogOpen}
        onOpenChange={setSessionDialogOpen}
        editingSession={editingSession}
        sessionForm={sessionForm}
        setSessionForm={setSessionForm}
        trainers={trainers}
        recommendations={recommendations}
        conflictWarning={conflictWarning}
        availablePos={availablePos}
        categories={taxonomyCategories}
        onFetchRecommendations={fetchRecommendations}
        onCheckConflict={checkConflict}
        onSelectRecommendedTrainer={handleSelectRecommendedTrainer}
        onSave={handleSaveSession}
      />

      <SessionFeedbackDialog
        open={sessionFeedbackDialogOpen}
        onOpenChange={setSessionFeedbackDialogOpen}
        session={selectedSessionForFeedback}
        form={sessionFeedbackForm}
        setForm={setSessionFeedbackForm}
        onSave={handleSaveSessionFeedback}
      />

      <ProjectFeedbackDialog
        open={projectFeedbackDialogOpen}
        onOpenChange={setProjectFeedbackDialogOpen}
        form={projectFeedbackForm}
        setForm={setProjectFeedbackForm}
        onSave={handleSaveProjectFeedback}
      />

      <BulkImportSessionsDialog
        open={bulkImportDialogOpen}
        onOpenChange={(open) => { if (!open) { setBulkImportDialogOpen(false); setBulkImportFile(null); setBulkImportResults(null); } }}
        file={bulkImportFile}
        onFileChange={(e) => { setBulkImportFile(e.target.files[0]); setBulkImportResults(null); }}
        results={bulkImportResults}
        loading={bulkImportLoading}
        onDownloadTemplate={handleDownloadTemplate}
        onImport={handleBulkImport}
      />

      <EmailReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        loading={reportLoading}
        html={reportHtml}
        filename="training_report.html"
        testIdPrefix="pd"
      />
    </div>
  );
};

export default TrainingProjectDetailPage;
